import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckSquare, Edit, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  xxLargeTextSize,
  baseTextSize,
  borderPrimary,
  rounded,
} from '../../../styling/stylingHelper';
import {
  fetchMepSectionById,
  updateMepTasks,
  completeMepSection,
  MepSection,
  MepTasks,
  MepTaskItem,
  MepTaskComponent,
  MepStockStatus,
} from '../../../services/mep/mepService';
import { fetchRecipesByRestaurant, Recipe, getTranslatedText, SupportedLanguage } from '../../../services/recipes/recipesService';
import { useAuth } from '../../../services/authentication/authContext';

// Stock status colors
const STOCK_STATUS_COLORS = {
  low: '#FFA514',  // Yellow
  out: '#F93B3B', // Red
  done: '#65FA20', // Green
};

const MepDetailPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const { backendUser } = useAuth();

  const [mepSection, setMepSection] = useState<MepSection | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isEditMode, setIsEditMode] = useState(false);

  // Check if we're in creation flow (no task items yet). Custom items make items
  // non-empty, so a custom-only section shows the execution view (matches the app).
  const isCreationFlow = !mepSection || mepSection.mepTasks.items.length === 0;

  // Load MEP section and recipes
  const loadData = useCallback(async () => {
    if (!sectionId) return;

    try {
      setLoading(true);
      const section = await fetchMepSectionById(sectionId);
      setMepSection(section);
      
      // Load recipes for the restaurant
      if (section.restaurantId) {
        const restaurantRecipes = await fetchRecipesByRestaurant(section.restaurantId);
        setRecipes(restaurantRecipes);
      }
    } catch (error) {
      console.error('Failed to load MEP section:', error);
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get recipe for a menu item name
  const getRecipeForMenuItem = (menuItemName: string): Recipe | undefined => {
    return recipes.find((r) => r.menuItemName === menuItemName);
  };

  // Toggle item expansion
  const toggleItem = (itemKey: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  // Handle component toggle in execution flow (with optimistic updates)
  const handleExecutionComponentToggle = async (
    itemIndex: number,
    componentIndex: number
  ) => {
    if (!mepSection) return;

    const currentTasks = mepSection.mepTasks;
    const currentItem = currentTasks.items[itemIndex];

    // Optimistic update: update UI immediately
    const updatedComponents = currentItem.components.map((comp, idx) => {
      if (idx === componentIndex) {
        if (comp.checked) {
          // Uncheck: remove checkedBy and checkedAt
          return {
            ...comp,
            checked: false,
            checkedBy: undefined,
            checkedAt: undefined,
          };
        } else {
          // Check: add checkedBy and checkedAt
          return {
            ...comp,
            checked: true,
            checkedBy: backendUser?.id || '',
            checkedAt: new Date().toISOString(),
          };
        }
      }
      return comp;
    });

    const updatedItems = currentTasks.items.map((item, idx) => {
      if (idx === itemIndex) {
        return {
          ...item,
          components: updatedComponents,
        };
      }
      return item;
    });

    const updatedTasks: MepTasks = {
      ...currentTasks,
      items: updatedItems,
    };

    const updatedSection: MepSection = {
      ...mepSection,
      mepTasks: updatedTasks,
    };

    // Update local state optimistically
    setMepSection(updatedSection);

    try {
      // Persist to backend
      const savedSection = await updateMepTasks(mepSection.id, updatedTasks);

      // Compare component states - only update if there are meaningful differences
      const componentsMatch =
        savedSection.mepTasks.items.length === updatedTasks.items.length &&
        savedSection.mepTasks.items.every((savedItem, itemIdx) => {
          const optimisticItem = updatedTasks.items[itemIdx];
          if (
            savedItem.recipeId !== optimisticItem.recipeId ||
            savedItem.menuItemName !== optimisticItem.menuItemName ||
            savedItem.components.length !== optimisticItem.components.length
          ) {
            return false;
          }
          return savedItem.components.every((savedComp, compIdx) => {
            const optimisticComp = optimisticItem.components[compIdx];
            return (
              savedComp.componentId === optimisticComp.componentId &&
              savedComp.checked === optimisticComp.checked &&
              savedComp.checkedBy === optimisticComp.checkedBy
            );
          });
        });

      if (!componentsMatch) {
        // There were changes from other users, update with server data
        setMepSection(savedSection);
      }
      // If components match, keep the optimistic update (no rerender needed)
    } catch (error) {
      console.error('Failed to update component:', error);
      // Revert optimistic update on error
      setMepSection(mepSection);
    }
  };

  // Handle creating MEP tasks from creation flow
  // selectedComponents is keyed by componentId
  const handleCreateMepTasks = async (selectedComponents: { [menuItemName: string]: { [componentId: string]: MepStockStatus } }) => {
    if (!mepSection) return;

    // Build mep_tasks structure
    const tasksDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const items: MepTaskItem[] = [];

    mepSection.menuItems.forEach((menuItemName) => {
      const recipe = getRecipeForMenuItem(menuItemName);
      if (!recipe) return;

      const menuItemComponents = selectedComponents[menuItemName] || {};
      const selectedComponentIds = Object.keys(menuItemComponents).filter(
        (id) => menuItemComponents[id] !== null
      );
      if (selectedComponentIds.length === 0) return;

      const components: MepTaskComponent[] = recipe.components
        .filter((comp) => selectedComponentIds.includes(comp.id))
        .map((comp) => ({
          componentId: comp.id,
          checked: false,
          stockStatus: menuItemComponents[comp.id],
        }));

      if (components.length > 0) {
        items.push({
          recipeId: recipe.id,
          menuItemName,
          components,
        });
      }
    });

    const newTasks: MepTasks = {
      tasksDate,
      items,
    };

    try {
      const savedSection = await updateMepTasks(mepSection.id, newTasks);
      setMepSection(savedSection);
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to create MEP tasks:', error);
    }
  };

  // Handle saving edit selection
  // selectedComponents is keyed by componentId
  const handleSaveEditSelection = async (selectedComponents: { [menuItemName: string]: { [componentId: string]: MepStockStatus } }) => {
    if (!mepSection) return;

    // Build updated mep_tasks structure preserving existing component checked states
    const tasksDate = mepSection.mepTasks.tasksDate || new Date().toISOString().split('T')[0];
    const items: MepTaskItem[] = [];

    // Create a map of existing component states by menu item and componentId
    const existingStates: { [key: string]: MepTaskComponent } = {};
    mepSection.mepTasks.items.forEach((item) => {
      item.components.forEach((comp) => {
        const key = `${item.menuItemName}:${comp.componentId}`;
        existingStates[key] = comp;
      });
    });

    mepSection.menuItems.forEach((menuItemName) => {
      const recipe = getRecipeForMenuItem(menuItemName);
      if (!recipe) return;

      const menuItemComponents = selectedComponents[menuItemName] || {};
      const selectedComponentIds = Object.keys(menuItemComponents).filter(
        (id) => menuItemComponents[id] !== null
      );
      if (selectedComponentIds.length === 0) return;

      const components: MepTaskComponent[] = recipe.components
        .filter((comp) => selectedComponentIds.includes(comp.id))
        .map((comp) => {
          const key = `${menuItemName}:${comp.id}`;
          const existingState = existingStates[key];
          // Preserve existing checked state but update stockStatus
          if (existingState) {
            return {
              ...existingState,
              stockStatus: menuItemComponents[comp.id],
            };
          }
          // New component starts unchecked
          return {
            componentId: comp.id,
            checked: false,
            stockStatus: menuItemComponents[comp.id],
          };
        });

      if (components.length > 0) {
        items.push({
          recipeId: recipe.id,
          menuItemName,
          components,
        });
      }
    });

    const updatedTasks: MepTasks = {
      tasksDate,
      items,
    };

    try {
      const savedSection = await updateMepTasks(mepSection.id, updatedTasks);
      setMepSection(savedSection);
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to save edit selection:', error);
    }
  };

  // Handle completing MEP section
  const handleCompleteMep = async () => {
    if (!mepSection) return;

    // Optimistic update
    const resetTasks = { tasksDate: null, items: [] };
    const updatedSection = {
      ...mepSection,
      mepTasks: resetTasks,
    };
    setMepSection(updatedSection);

    try {
      const savedSection = await completeMepSection(mepSection.id);
      setMepSection(savedSection);
      // Navigate back to MEP list page after successful completion
      navigate('/mep');
    } catch (error) {
      console.error('Failed to complete MEP:', error);
      // Revert on error
      setMepSection(mepSection);
    }
  };

  if (loading) {
    return (
      <div className={cn(baseTextSize, secondaryText, 'text-center py-10')}>
        <p>{t('mep.loading')}</p>
      </div>
    );
  }

  if (!mepSection) {
    return (
      <div className={cn(baseTextSize, secondaryText, 'text-center py-10')}>
        <p>{t('mep.sectionNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/mep')}
            className={cn(
              'p-2 rounded-lg',
              'border',
              borderPrimary,
              'hover:bg-gray-50 dark:hover:bg-gray-800',
              secondaryText
            )}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText)}>
            {getTranslatedText(mepSection.sectionNameTranslations, lang)}
          </h1>
        </div>

        {!isCreationFlow && (
          <div className="flex items-center gap-2 mb-4">
            {!isEditMode && (
              <button
                onClick={() => setIsEditMode(true)}
                className={cn(
                  smallTextSize,
                  'px-3 py-1.5 rounded-lg',
                  'border',
                  borderPrimary,
                  secondaryText,
                  'hover:bg-gray-50 dark:hover:bg-gray-800',
                  'flex items-center gap-1.5'
                )}
              >
                <Edit className="w-4 h-4" />
                <span>{t('mep.editSelection')}</span>
              </button>
            )}
            <button
              onClick={handleCompleteMep}
              className={cn(
                smallTextSize,
                'px-3 py-1.5 rounded-lg',
                'border',
                borderPrimary,
                secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800',
                'flex items-center gap-1.5'
              )}
            >
              <CheckSquare className="w-4 h-4" />
              <span>{t('mep.completeMep')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {isCreationFlow ? (
        <CreationFlowView
          mepSection={mepSection}
          getRecipeForMenuItem={getRecipeForMenuItem}
          onCreateMep={handleCreateMepTasks}
          expandedItems={expandedItems}
          toggleItem={toggleItem}
        />
      ) : (
        <ExecutionFlowView
          mepSection={mepSection}
          getRecipeForMenuItem={getRecipeForMenuItem}
          onComponentToggle={handleExecutionComponentToggle}
          expandedItems={expandedItems}
          toggleItem={toggleItem}
          isEditMode={isEditMode}
          onSaveEdit={handleSaveEditSelection}
          onCancelEdit={() => setIsEditMode(false)}
        />
      )}
    </div>
  );
};

// Helper to cycle stock status: null -> 'low' -> 'out' -> null
const cycleStockStatus = (current: MepStockStatus): MepStockStatus => {
  if (current === null || current === undefined) return 'low';
  if (current === 'low') return 'out';
  return null;
};

// Stock status badge component (for labels)
const StockStatusBadge = ({ status }: { status: MepStockStatus }) => {
  const { t } = useTranslation();
  if (status === 'low') {
    return (
      <span
        className="text-xs px-1.5 py-0.5 rounded font-medium"
        style={{ backgroundColor: STOCK_STATUS_COLORS.low, color: '#000' }}
      >
        {t('mep.stockStatus.low')}
      </span>
    );
  }
  if (status === 'out') {
    return (
      <span
        className="text-xs px-1.5 py-0.5 rounded font-medium"
        style={{ backgroundColor: STOCK_STATUS_COLORS.out, color: '#fff' }}
      >
        {t('mep.stockStatus.out')}
      </span>
    );
  }
  return null;
};

// Creation Flow Component
interface CreationFlowViewProps {
  mepSection: MepSection;
  getRecipeForMenuItem: (name: string) => Recipe | undefined;
  onCreateMep: (selectedComponents: { [menuItemName: string]: { [componentId: string]: MepStockStatus } }) => void;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
}

const CreationFlowView = ({
  mepSection,
  getRecipeForMenuItem,
  onCreateMep,
  expandedItems,
  toggleItem,
}: CreationFlowViewProps) => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  // Track stock status per component: { menuItemName: { componentId: 'low' | 'out' | null } }
  const [selectedComponents, setSelectedComponents] = useState<{ [menuItemName: string]: { [componentId: string]: MepStockStatus } }>({});

  const handleComponentToggle = (menuItemName: string, componentId: string) => {
    setSelectedComponents((prev) => {
      const itemComponents = prev[menuItemName] || {};
      const currentStatus = itemComponents[componentId] || null;
      const newStatus = cycleStockStatus(currentStatus);

      return {
        ...prev,
        [menuItemName]: {
          ...itemComponents,
          [componentId]: newStatus,
        },
      };
    });
  };

  // Count selected components (those with non-null status)
  const getSelectedCount = (menuItemName: string): number => {
    const itemComponents = selectedComponents[menuItemName] || {};
    return Object.values(itemComponents).filter((status) => status !== null).length;
  };

  // Check if any components are selected
  const hasAnySelected = Object.values(selectedComponents).some((itemComponents) =>
    Object.values(itemComponents).some((status) => status !== null)
  );

  return (
    <div>
      <p className={cn(baseTextSize, secondaryText, 'mb-4')}>
        {t('mep.creationFlowDescription')}
      </p>

      <div className="space-y-3">
        {mepSection.menuItems.map((menuItemName) => {
          const recipe = getRecipeForMenuItem(menuItemName);
          const itemKey = `creation-${menuItemName}`;
          const isExpanded = expandedItems.has(itemKey);
          const selectedCount = getSelectedCount(menuItemName);

          if (!recipe || recipe.components.length === 0) {
            return (
              <div
                key={menuItemName}
                className={cn(
                  'border',
                  borderPrimary,
                  rounded,
                  'bg-card dark:bg-card-dark',
                  'p-4'
                )}
              >
                <p className={cn(smallTextSize, secondaryText)}>
                  {menuItemName} - {t('mep.noRecipeFound')}
                </p>
              </div>
            );
          }

          return (
            <div
              key={menuItemName}
              className={cn(
                'border',
                borderPrimary,
                rounded,
                'bg-card dark:bg-card-dark'
              )}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleItem(itemKey)}
              >
                <div className="flex items-center justify-between">
                  <h3 className={cn(baseTextSize, semiBoldText, primaryText)}>
                    {menuItemName}
                  </h3>
                  {isExpanded ? (
                    <ChevronUp className={cn('w-5 h-5', secondaryText)} />
                  ) : (
                    <ChevronDown className={cn('w-5 h-5', secondaryText)} />
                  )}
                </div>
                <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                  {selectedCount} / {recipe.components.length} {t('mep.components')} selected
                </p>
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-zinc-200 dark:border-zinc-700 pt-4">
                  <div className="space-y-2">
                    {recipe.components.map((component) => {
                      const itemComponents = selectedComponents[menuItemName] || {};
                      const stockStatus = itemComponents[component.id] || null;
                      const isSelected = stockStatus !== null;
                      const componentName = getTranslatedText(component.nameTranslations, lang);
                      return (
                        <div
                          key={component.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                          onClick={() => handleComponentToggle(menuItemName, component.id)}
                        >
                          <div className="flex-1 flex items-center gap-2">
                            <p className={cn(smallTextSize, isSelected ? primaryText : secondaryText)}>
                              {componentName}
                            </p>
                            {stockStatus && <StockStatusBadge status={stockStatus} />}
                          </div>
                          <div
                            className={cn(
                              'w-5 h-5 rounded border-2 flex items-center justify-center',
                              isSelected ? 'border-transparent' : 'border-gray-300 dark:border-gray-600'
                            )}
                            style={isSelected ? {
                              backgroundColor: stockStatus === 'out' ? STOCK_STATUS_COLORS.out : STOCK_STATUS_COLORS.low
                            } : {}}
                          >
                            {isSelected && (
                              <span className="text-xs font-bold text-white dark:text-white">
                                {stockStatus === 'out' ? '!' : '•'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={() => onCreateMep(selectedComponents)}
          disabled={!hasAnySelected}
          className={cn(
            smallTextSize,
            'text-white dark:text-white',
            semiBoldText,
            'bg-darkGreenColor dark:bg-darkGreenColor-dark',
            'rounded-xl',
            'px-6 py-3',
            'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
            'flex items-center gap-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <CheckSquare className="w-5 h-5" />
          <span>{t('mep.createMep')}</span>
        </button>
      </div>
    </div>
  );
};

// Helper to get item urgency score (higher = more urgent)
const getItemUrgencyScore = (item: MepTaskItem): number => {
  let outCount = 0;
  let lowCount = 0;
  let uncheckedCount = 0;

  item.components.forEach((comp) => {
    if (!comp.checked) {
      uncheckedCount++;
      if (comp.stockStatus === 'out') outCount++;
      else if (comp.stockStatus === 'low') lowCount++;
    }
  });

  // Priority: out items first, then low items, then by unchecked count
  return outCount * 10000 + lowCount * 100 + uncheckedCount;
};

// Helper to get item status color
const getItemStatusColor = (item: MepTaskItem): string => {
  const hasOut = item.components.some((c) => !c.checked && c.stockStatus === 'out');
  const hasLow = item.components.some((c) => !c.checked && c.stockStatus === 'low');
  const allChecked = item.components.every((c) => c.checked);

  if (hasOut) return STOCK_STATUS_COLORS.out;
  if (hasLow) return STOCK_STATUS_COLORS.low;
  if (allChecked) return STOCK_STATUS_COLORS.done;
  return STOCK_STATUS_COLORS.done; // Default to green if no stock status set
};

// Execution Flow Component
interface ExecutionFlowViewProps {
  mepSection: MepSection;
  getRecipeForMenuItem: (name: string) => Recipe | undefined;
  onComponentToggle: (itemIndex: number, componentIndex: number) => void;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
  isEditMode: boolean;
  onSaveEdit: (selectedComponents: { [menuItemName: string]: { [componentId: string]: MepStockStatus } }) => void;
  onCancelEdit: () => void;
}

const ExecutionFlowView = ({
  mepSection,
  getRecipeForMenuItem,
  onComponentToggle,
  expandedItems,
  toggleItem,
  isEditMode,
  onSaveEdit,
  onCancelEdit,
}: ExecutionFlowViewProps) => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  // Track stock status per component: { menuItemName: { componentId: 'low' | 'out' | null } }
  const [selectedComponents, setSelectedComponents] = useState<{ [menuItemName: string]: { [componentId: string]: MepStockStatus } }>({});

  // Initialize selected components from existing mep_tasks when entering edit mode
  useEffect(() => {
    if (isEditMode) {
      const initialSelection: { [menuItemName: string]: { [componentId: string]: MepStockStatus } } = {};
      mepSection.mepTasks.items.forEach((item) => {
        initialSelection[item.menuItemName] = {};
        item.components.forEach((comp) => {
          initialSelection[item.menuItemName][comp.componentId] = comp.stockStatus || 'low';
        });
      });
      setSelectedComponents(initialSelection);
    }
  }, [isEditMode, mepSection.mepTasks.items]);

  const handleComponentToggle = (menuItemName: string, componentId: string) => {
    setSelectedComponents((prev) => {
      const itemComponents = prev[menuItemName] || {};
      const currentStatus = itemComponents[componentId] || null;
      const newStatus = cycleStockStatus(currentStatus);

      return {
        ...prev,
        [menuItemName]: {
          ...itemComponents,
          [componentId]: newStatus,
        },
      };
    });
  };

  // Count selected components (those with non-null status)
  const getSelectedCount = (menuItemName: string): number => {
    const itemComponents = selectedComponents[menuItemName] || {};
    return Object.values(itemComponents).filter((status) => status !== null).length;
  };

  // Sort items by urgency (most urgent first)
  const sortedItems = useMemo((): MepTaskItem[] => {
    return [...mepSection.mepTasks.items].sort((a, b) => getItemUrgencyScore(b) - getItemUrgencyScore(a));
  }, [mepSection.mepTasks.items]);

  // Get original index for a sorted item
  const getOriginalIndex = (item: MepTaskItem): number => {
    return mepSection.mepTasks.items.findIndex(
      (i) => i.recipeId === item.recipeId && i.menuItemName === item.menuItemName
    );
  };

  // If in edit mode, show the edit interface
  if (isEditMode) {
    return (
      <div>
        <p className={cn(baseTextSize, secondaryText, 'mb-4')}>
          {t('mep.editSelectionDescription') || 'Click components to cycle through stock status: Low (yellow) → Out (red) → Remove.'}
        </p>

        <div className="space-y-3">
          {mepSection.menuItems.map((menuItemName) => {
            const recipe = getRecipeForMenuItem(menuItemName);
            const itemKey = `edit-${menuItemName}`;
            const isExpanded = expandedItems.has(itemKey);
            const selectedCount = getSelectedCount(menuItemName);

            if (!recipe || recipe.components.length === 0) {
              return (
                <div
                  key={menuItemName}
                  className={cn(
                    'border',
                    borderPrimary,
                    rounded,
                    'bg-card dark:bg-card-dark',
                    'p-4'
                  )}
                >
                  <p className={cn(smallTextSize, secondaryText)}>
                    {menuItemName} - {t('mep.noRecipeFound')}
                  </p>
                </div>
              );
            }

            return (
              <div
                key={menuItemName}
                className={cn(
                  'border',
                  borderPrimary,
                  rounded,
                  'bg-card dark:bg-card-dark'
                )}
              >
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleItem(itemKey)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className={cn(baseTextSize, semiBoldText, primaryText)}>
                      {menuItemName}
                    </h3>
                    {isExpanded ? (
                      <ChevronUp className={cn('w-5 h-5', secondaryText)} />
                    ) : (
                      <ChevronDown className={cn('w-5 h-5', secondaryText)} />
                    )}
                  </div>
                  <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                    {selectedCount} / {recipe.components.length} {t('mep.components')} selected
                  </p>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-zinc-200 dark:border-zinc-700 pt-4">
                    <div className="space-y-2">
                      {recipe.components.map((component) => {
                        const itemComponents = selectedComponents[menuItemName] || {};
                        const stockStatus = itemComponents[component.id] || null;
                        const isSelected = stockStatus !== null;
                        const componentName = getTranslatedText(component.nameTranslations, lang);
                        // Check existing component state
                        const existingItem = mepSection.mepTasks.items.find(
                          (item) => item.menuItemName === menuItemName
                        );
                        const existingComponent = existingItem?.components.find(
                          (comp) => comp.componentId === component.id
                        );

                        return (
                          <div
                            key={component.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            onClick={() => handleComponentToggle(menuItemName, component.id)}
                          >
                            <div className="flex-1 flex items-center gap-2">
                              <p className={cn(smallTextSize, isSelected ? primaryText : secondaryText)}>
                                {componentName}
                              </p>
                              {stockStatus && <StockStatusBadge status={stockStatus} />}
                              {existingComponent?.checked && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                  {t('mep.checked') || 'Done'}
                                </span>
                              )}
                            </div>
                            <div
                              className={cn(
                                'w-5 h-5 rounded border-2 flex items-center justify-center',
                                isSelected ? 'border-transparent' : 'border-gray-300 dark:border-gray-600'
                              )}
                              style={isSelected ? {
                                backgroundColor: stockStatus === 'out' ? STOCK_STATUS_COLORS.out : STOCK_STATUS_COLORS.low
                              } : {}}
                            >
                              {isSelected && (
                                <span className="text-xs font-bold text-white dark:text-white">
                                  {stockStatus === 'out' ? '!' : '•'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancelEdit}
            className={cn(
              smallTextSize,
              'px-6 py-3 rounded-xl',
              'border',
              borderPrimary,
              secondaryText,
              'hover:bg-gray-50 dark:hover:bg-gray-800'
            )}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onSaveEdit(selectedComponents)}
            className={cn(
              smallTextSize,
              'text-white dark:text-white',
              semiBoldText,
              'bg-darkGreenColor dark:bg-darkGreenColor-dark',
              'rounded-xl',
              'px-6 py-3',
              'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
              'flex items-center gap-2'
            )}
          >
            <CheckSquare className="w-5 h-5" />
            <span>{t('common.save')}</span>
          </button>
        </div>
      </div>
    );
  }

  // Normal execution flow view - sorted by urgency
  return (
    <div>
      {mepSection.mepTasks.tasksDate && (
        <p className={cn(baseTextSize, secondaryText, 'mb-4')}>
          {t('mep.tasksDate')}: {new Date(mepSection.mepTasks.tasksDate).toLocaleDateString()}
        </p>
      )}

      <div className="space-y-3">
        {sortedItems.map((item: MepTaskItem, sortedIndex: number) => {
          const originalIndex = getOriginalIndex(item);
          const itemKey = `execution-${item.recipeId}-${sortedIndex}`;
          const isExpanded = expandedItems.has(itemKey);
          const checkedCount = item.components.filter((c: MepTaskComponent) => c.checked).length;
          const totalCount = item.components.length;
          const statusColor = getItemStatusColor(item);

          return (
            <div
              key={itemKey}
              className={cn(
                'border',
                borderPrimary,
                rounded,
                'bg-card dark:bg-card-dark'
              )}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleItem(itemKey)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: statusColor }}
                    />
                    <h3 className={cn(baseTextSize, semiBoldText, primaryText)}>
                      {item.menuItemName}
                    </h3>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className={cn('w-5 h-5', secondaryText)} />
                  ) : (
                    <ChevronDown className={cn('w-5 h-5', secondaryText)} />
                  )}
                </div>
                <p className={cn(smallTextSize, secondaryText, 'mt-1 ml-6')}>
                  {checkedCount} / {totalCount} {t('mep.components')} completed
                </p>
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-zinc-200 dark:border-zinc-700 pt-4">
                  <div className="space-y-2">
                    {item.components.map((component: MepTaskComponent, componentIndex: number) => {
                      // Look up component name from recipe; fall back to the stored
                      // name snapshot (custom free-text items carry their own nameTranslations).
                      const recipe = getRecipeForMenuItem(item.menuItemName);
                      const recipeComponent = recipe?.components.find(c => c.id === component.componentId);
                      const componentName = recipeComponent
                        ? getTranslatedText(recipeComponent.nameTranslations, lang)
                        : component.nameTranslations
                          ? getTranslatedText(component.nameTranslations, lang)
                          : component.componentId;

                      return (
                        <div
                          key={component.componentId}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <div className="flex items-center gap-2">
                            {component.stockStatus && <StockStatusBadge status={component.stockStatus} />}
                          </div>
                          <input
                            type="checkbox"
                            checked={component.checked}
                            onChange={() => onComponentToggle(originalIndex, componentIndex)}
                            className="w-4 h-4 rounded border-gray-300 dark:border-borderPrimary-dark text-green-600 dark:text-textGreen-dark focus:ring-green-500 dark:focus:ring-green-500"
                          />
                          <div className="flex-1">
                            <p
                              className={cn(
                                smallTextSize,
                                component.checked
                                  ? 'line-through text-gray-400 dark:text-textQuaternary-dark'
                                  : primaryText
                              )}
                            >
                              {componentName}
                            </p>
                            {component.checked && component.checkedAt && (
                              <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                                {t('mep.checkedAt')}{' '}
                                {new Date(component.checkedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MepDetailPage;
